import DataGrid, { Column } from "devextreme-react/data-grid";
import DataSource from "devextreme/data/data_source";
import React, { useCallback, useContext, useMemo, useRef } from "react";
import { TriggerContext } from "../../context/TriggerProvider";
import { ValueContext } from "../../context/ValueProvider";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import PhotoCell from "./CellRender/PhotoCell";
import { Container } from "./styles";
import { useAuth } from "../../hooks/auth";
import pdsGroups from "../../config/pdsGroups";

export default function MenteeEvaluationList({
  isSidePanelFullScreen,
  evaluation,
  idEvaluation,
}) {
  const { master } = useContext(ValueContext);
  const { addToast } = useToast();
  const dataGridRef = useRef(null);
  const { triggersFunction } = useContext(TriggerContext);
  const { hasScope } = useAuth();
  const isAdmin = useMemo(() => hasScope([pdsGroups.Admin]), [hasScope]);

  const evaluationMenteeSourceByTeamDataSource = useMemo(() => {
    return new DataSource({
      load: async () => {
        try {
          const { data: menteeData } = await api.get(`api/professional-mentor`);
          const mentees =
            menteeData?.filter(
              (item) =>
                item?.professionalMentor?.idMentor ===
                evaluation?.idProfessional
            ) || [];

          if (mentees.length === 0) {
            return [];
          }

          const evaluationPromises = mentees.map(async (mentee) => {
            try {
              const { data: evaluationData } = await api.get(
                `api/evaluations/${idEvaluation}/mentee/${mentee.id}`
              );
              const { id: evaluationId, ...otherEvaluationData } =
                evaluationData || {};

              return {
                ...mentee,
                evaluationId,
                ...otherEvaluationData,
                hasEvaluation: true,
                error: null,
              };
            } catch (evaluationError) {
              return {
                ...mentee,
                evaluationId: null,
                idEvaluation: null,
                idMentee: mentee.id,
                menteeEvaluation: null,
                doYouFeelComfortable: null,
                isApplicable: null,
                menteeDevelopmentPoints: null,
                hasEvaluation: false,
                error: evaluationError.message || "Failed to load evaluation",
              };
            }
          });

          const detailedMentees = await Promise.allSettled(evaluationPromises);

          const processedMentees = detailedMentees
            .map((result, index) => {
              if (result.status === "fulfilled") {
                return result.value;
              } else {
                return {
                  ...mentees[index],
                  evaluationId: null,
                  idEvaluation: null,
                  idMentee: mentees[index].id,
                  menteeEvaluation: null,
                  doYouFeelComfortable: null,
                  isApplicable: null,
                  menteeDevelopmentPoints: null,
                  hasEvaluation: false,
                  error: result.reason?.message || "Unknown error",
                };
              }
            })
            .filter(Boolean);

          const failedCount = processedMentees.filter(
            (m) => !m.hasEvaluation
          ).length;
          if (failedCount > 0) {
            addToast({
              type: "warning",
              title: `${failedCount} mentee evaluation(s) couldn't be loaded`,
            });
          }

          return processedMentees;
        } catch (err) {
          addToast({
            type: "error",
            title: "Mentee's evaluations couldn't be loaded. Try again later",
            description: err.message || "Unknown error occurred",
          });
          return [];
        }
      },

      update: async (key, values) => {
        try {
          await api.put(
            `api/evaluations/${idEvaluation}/mentee/${key.id}`,
            values
          );
          triggersFunction("updateGrade");
        } catch (err) {
          addToast({
            type: "error",
            title: "Error updating mentee evaluation",
          });
          throw err;
        }
      },

      reshapeOnPush: true,
      loadMode: "raw",
      cacheRawData: true,
    });
  }, [addToast, evaluation.idProfessional, idEvaluation, triggersFunction]);

  const renderPhotoCell = useCallback(
    (e) => {
      const matchedProfessional = master?.professionals?.find(
        (professional) => professional.ID === (e.data.idMentee || e.data.id)
      );
      return <PhotoCell data={matchedProfessional} />;
    },
    [master]
  );

  // Função de validação para os campos de texto
  const validateTextFields = (e) => {
    // Combina os dados antigos com os novos dados
    const rowData = Object.assign({}, e.oldData, e.newData);

    const menteeDevelopmentPoints = rowData.menteeDevelopmentPoints || "";

    // Verifica se o campo obrigatório está preenchido
    if (!menteeDevelopmentPoints.trim()) {
      e.isValid = false;
      e.errorText = "Fill all the text boxes to save this input";
    }
  };

  return (
    <Container
      style={{ width: !isSidePanelFullScreen ? "550px" : "100%", height: 450 }}
    >
      <DataGrid
        dataSource={evaluationMenteeSourceByTeamDataSource}
        ref={dataGridRef}
        key="id"
        height="100%"
        width="100%"
        headerFilter={{ visible: true, allowSearch: true }}
        onRowClick={(e) => {
          if (e.rowType === "data" && isAdmin) {
            dataGridRef.current.instance.editRow(e.rowIndex);
          }
        }}
        onRowValidating={validateTextFields}
        editing={{
          mode: "popup",
          allowUpdating: true,
          form: {
            items: [
              {
                dataField: "id",
                colSpan: 2,
              },
              {
                dataField: "isApplicable",
                editorType: "dxCheckBox",
                editorOptions: {
                  allowIndeterminateState: false,
                },
              },
              {
                dataField: "doYouFeelComfortable",
                editorType: "dxCheckBox",
                editorOptions: {
                  allowIndeterminateState: false,
                },
              },
              {
                dataField: "menteeEvaluation",
                editorType: "dxTextArea",
                colSpan: 2,
              },
              {
                dataField: "menteeDevelopmentPoints",
                editorType: "dxTextArea",
                colSpan: 2,
                isRequired: true,
                validationRules: [
                  {
                    type: "required",
                    message: "This field is required",
                  },
                ],
              },
            ],
          },
          popup: {
            title: "Edit Evaluation",
            showTitle: true,
            width: 700,
            height: 584,
          },
        }}
      >
        <Column
          dataField="id"
          caption="Mentee"
          alignment="center"
          cellRender={renderPhotoCell}
          lookup={{
            dataSource: master.professionals,
            displayExpr: "Name",
            valueExpr: "ID",
          }}
        />
        <Column
          dataField="isActive"
          caption="Is Active?"
          dataType="boolean"
          alignment="center"
        />
        <Column
          dataField="isApplicable"
          caption="Considered?"
          dataType="boolean"
          alignment="center"
          visible={false}
        />
        <Column
          dataField="doYouFeelComfortable"
          caption="Do you feel comfortable to evaluate this professional?"
          dataType="boolean"
          alignment="center"
          visible={false}
        />
        <Column
          dataField="menteeEvaluation"
          caption="Did the professional contribute to your professional development during this cycle? Please, give examples of positive contributions."
          dataType="string"
          editorType="dxTextArea"
          visible={false}
        />
        <Column
          dataField="menteeDevelopmentPoints"
          caption="Do you see any development point, improvement or adjustments in the dynamic with your mentor? Please explain."
          dataType="string"
          editorType="dxTextArea"
          visible={false}
        />
      </DataGrid>
    </Container>
  );
}
